import { useEffect, useRef, useState } from "react";
import { PDFDocumentProxy, getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import * as pdfjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";
import "pdfjs-dist/web/pdf_viewer.css";

const workerSrc = "/scripts/pdf.worker.min.js";
GlobalWorkerOptions.workerSrc = workerSrc;

function PdfSimpleViewer({
  fileUrl,
  initialPage
}: Readonly<{
  fileUrl: string;
  initialPage?: number;
}>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let pdfViewer: pdfjsViewer.PDFViewer;
    let pdfDocument: PDFDocumentProxy | null = null;
    const container = containerRef.current;

    const load = async () => {
      // Both refs target unconditionally-rendered divs, so after mount they are
      // always populated; this null guard only fires on an unmount race.
      /* v8 ignore next */
      if (!container || !viewerRef.current) return;

      setLoading(true);
      const loadingTask = getDocument({ url: fileUrl });
      pdfDocument = await loadingTask.promise;

      const eventBus = new pdfjsViewer.EventBus();
      const linkService = new pdfjsViewer.PDFLinkService({
        eventBus
      });

      pdfViewer = new pdfjsViewer.PDFViewer({
        container,
        viewer: viewerRef.current,
        linkService,
        eventBus,
        removePageBorders: true
      } as any);

      linkService.setDocument(pdfDocument, null);
      linkService.setViewer(pdfViewer);
      pdfViewer.setDocument(pdfDocument);

      pdfViewer.eventBus.on("pagesinit", () => {
        pdfViewer.currentScaleValue = "page-width";
        if (initialPage && initialPage > 0) {
          pdfViewer.currentPageNumber = initialPage + 1;
        }
        setLoading(false);
      });
    };

    load();

    return () => {
      if (pdfViewer) {
        if (pdfViewer.pdfDocument) {
          pdfViewer.pdfDocument.destroy();
        }
        pdfViewer.cleanup();
      }
    };
  }, [fileUrl, initialPage]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "auto"
      }}
      data-testid="pdf-simple-viewer"
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1
          }}
        >
          Loading...
        </div>
      )}
      <div ref={viewerRef} className="pdfViewer" />
    </div>
  );
}

export default PdfSimpleViewer;
